import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Button,
  Input,
  Textarea,
  Spinner,
  Alert,
  Checkbox,
  Select as MTSelect,
  Option,
  Collapse,
} from '@material-tailwind/react';
import Select from 'react-select';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { DocumentPlusIcon } from '@heroicons/react/24/outline';
import { useParams, useNavigate } from 'react-router-dom';

import { ReportEditor } from '../../components/report-editor';
import { generateDraft, downloadReport, getDraft } from '../../services/reportApi';
import { UserAuth } from '../../context/AuthContext';
import { UserData } from '../../context/UserContext';
import { DataContext } from '../../context/DataContext';

const YEAR_RANGES = [
  { value: 0, label: 'Exact year' },
  { value: 1, label: '± 1 year' },
  { value: 2, label: '± 2 years' },
  { value: 3, label: '± 3 years' },
  { value: 5, label: '± 5 years' },
  { value: -1, label: 'All years' },
];

const PERIOD_OPTIONS = [
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '180 days' },
  { value: 365, label: '1 year' },
  { value: 730, label: '2 years' },
  { value: 1095, label: '3 years' },
];

const AVAILABLE_SECTIONS = [
  { id: 'cover', label: 'Cover Page' },
  { id: 'tableOfContents', label: 'Table of Contents' },
  { id: 'about', label: 'About Lone Mountain' },
  { id: 'marketTrends', label: 'Market Trends' },
  { id: 'onMarketComps', label: 'On Market Comparables' },
  { id: 'recentlySoldComps', label: 'Recently Sold Comparables' },
  { id: 'summaryPricing', label: 'Summary & Pricing' },
  { id: 'strategy', label: 'Brokerage Services' },
  { id: 'assumptions', label: 'Thank You' },
];

function ReportEditorPage() {
  const { user } = UserAuth();
  const { userData } = UserData();
  const { draftId } = useParams();
  const navigate = useNavigate();

  // Get manufacturer/model data from context
  const dataContext = useContext(DataContext);
  const manufacturerData = dataContext.getManufacturerData();
  const modelData = dataContext.getModelData();

  const [draft, setDraft] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);
  const [error, setError] = useState(null);
  const [finalizedReportId, setFinalizedReportId] = useState(null);
  const [showSections, setShowSections] = useState(false);

  // Manufacturer/model select state
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);

  // Build manufacturer options from DataContext
  const manufacturerOptions = useMemo(() => {
    if (!Object.keys(manufacturerData).length) return [];
    return Object.keys(manufacturerData)
      .sort()
      .map((k) => ({ value: manufacturerData[k], label: k }));
  }, [manufacturerData]);

  // Build model options filtered by selected manufacturer
  const modelOptions = useMemo(() => {
    if (!Object.keys(modelData).length || !selectedManufacturer) return [];

    const options = [];
    for (const [key, value] of Object.entries(modelData)) {
      const manufacturersForModel = new Set(value.manufacturers);
      const manufacturerIds = new Set(selectedManufacturer.value?.ids || []);
      const hasIntersection = manufacturersForModel.intersection
        ? manufacturersForModel.intersection(manufacturerIds).size > 0
        : [...manufacturersForModel].some(id => manufacturerIds.has(id));

      if (hasIntersection) {
        options.push({ value: value, label: key });
      }
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [modelData, selectedManufacturer]);

  // Handle manufacturer change - clear model when manufacturer changes
  const handleManufacturerChange = (option) => {
    setSelectedManufacturer(option);
    setSelectedModel(null);
  };

  const [reportParams, setReportParams] = useState({
    customerName: '',
    subjectAircraftDetails: '',
    subjectAircraft: {
      registration: '',
      year: '',
      totalTime: '',
    },
    yearRange: 1,
    templateId: 'standard',
    includedSections: ['cover', 'tableOfContents', 'about', 'marketTrends', 'onMarketComps', 'recentlySoldComps', 'summaryPricing', 'strategy', 'assumptions'],
    analysisConfig: {
      periodDays: 90,
    },
    preparer: {
      name: '',
      email: '',
      phone: '',
      title: '',
    },
    teamId: '',
    sharedWithTeam: false,
  });

  // Auto-populate preparer and teamId from user profile
  useEffect(() => {
    setReportParams((prev) => ({
      ...prev,
      preparer: {
        name: user?.displayName || '',
        email: user?.email || '',
        phone: userData?.phoneNumber || '',
        title: userData?.title || '',
      },
      teamId: userData?.teamId || '',
    }));
  }, [user?.displayName, user?.email, userData?.phoneNumber, userData?.title, userData?.teamId]);

  // Load existing draft if draftId is provided
  useEffect(() => {
    if (draftId) {
      const loadExistingDraft = async () => {
        try {
          setIsLoadingDraft(true);
          setError(null);
          const draftData = await getDraft(draftId);
          console.log('Loaded draft:', draftData);
          setDraft(draftData);
        } catch (err) {
          console.error('Failed to load draft:', err);
          setError(err.message);
        } finally {
          setIsLoadingDraft(false);
        }
      };
      loadExistingDraft();
    }
  }, [draftId]);

  const handleSectionToggle = (sectionId) => {
    setReportParams((prev) => {
      const sections = prev.includedSections.includes(sectionId)
        ? prev.includedSections.filter((s) => s !== sectionId)
        : [...prev.includedSections, sectionId];
      return { ...prev, includedSections: sections };
    });
  };

  const handleGenerateDraft = async () => {
    try {
      setIsGenerating(true);
      setError(null);

      // Prepare params with proper types for API
      // Note: additionalDetails must be nested inside subjectAircraft, not top-level
      // Note: yearRange is now inside analysisConfig (0 = exact year match, -1 = all years -> null)
      const params = {
        customerName: reportParams.customerName,
        templateId: reportParams.templateId,
        includedSections: reportParams.includedSections,
        analysisConfig: {
          ...reportParams.analysisConfig,
          yearRange: reportParams.yearRange === -1 ? null : reportParams.yearRange,
        },
        preparer: reportParams.preparer,
        teamId: reportParams.teamId,
        sharedWithTeam: reportParams.sharedWithTeam,
        subjectAircraft: {
          registration: reportParams.subjectAircraft.registration,
          manufacturer: selectedManufacturer?.label || '',
          model: selectedModel?.label || '',
          year: parseInt(reportParams.subjectAircraft.year, 10),
          totalTime: parseInt(reportParams.subjectAircraft.totalTime, 10),
          additionalDetails: reportParams.subjectAircraftDetails, // Move to nested field
        },
      };

      const draftResponse = await generateDraft(params);
      console.log('Draft response:', draftResponse);
      if (draftResponse.draftId) {
        setDraft(draftResponse);
      } else {
        setError('No draft ID returned from server');
      }
    } catch (err) {
      console.error('Generate draft error:', err);
      setError(err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleFinalized = (result) => {
    console.log('Finalize result:', result);
    // Store the reportId to use for downloading
    const reportId = result.reportId || result.report_id || result.id;
    if (reportId) {
      setFinalizedReportId(reportId);
    } else {
      setError('No report ID returned from finalization');
    }
  };

  const handleDownload = async () => {
    if (!finalizedReportId) return;
    try {
      setIsDownloading(true);
      setError(null);
      const { downloadUrl } = await downloadReport(finalizedReportId);
      window.open(downloadUrl, '_blank');
    } catch (err) {
      console.error('Download error:', err);
      setError(err.message);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleNewReport = () => {
    setDraft(null);
    setFinalizedReportId(null);
    setError(null);
    setSelectedManufacturer(null);
    setSelectedModel(null);
    setReportParams({
      customerName: '',
      subjectAircraftDetails: '',
      subjectAircraft: {
        registration: '',
        year: '',
        totalTime: '',
      },
      yearRange: 1,
      templateId: 'standard',
      includedSections: ['cover', 'tableOfContents', 'about', 'marketTrends', 'onMarketComps', 'recentlySoldComps', 'summaryPricing', 'strategy', 'assumptions'],
      analysisConfig: {
        periodDays: 90,
      },
      preparer: {
        name: user?.displayName || '',
        email: user?.email || '',
        phone: userData?.phoneNumber || '',
        title: userData?.title || '',
      },
      teamId: userData?.teamId || '',
      sharedWithTeam: false,
    });
    // Navigate to base path without draftId
    navigate('/dashboard/report-editor');
  };

  const isFormValid =
    reportParams.customerName.trim() &&
    reportParams.templateId.trim() &&
    reportParams.includedSections.length > 0 &&
    reportParams.subjectAircraft.registration?.trim() &&
    selectedManufacturer?.label &&
    selectedModel?.label &&
    reportParams.subjectAircraft.year &&
    reportParams.subjectAircraft.totalTime;

  // Show PDF download after finalization
  if (finalizedReportId) {
    return (
      <div className="p-6">
        <Card>
          <CardBody className="flex flex-col items-center gap-4 py-12">
            <Typography variant="h4" color="green">
              Report Finalized!
            </Typography>
            <Typography color="gray">
              Your report has been generated successfully.
            </Typography>
            {error && (
              <Alert color="red" className="w-full max-w-md" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <div className="flex gap-4 mt-4">
              <Button variant="outlined" onClick={handleNewReport}>
                Create New Report
              </Button>
              <Button onClick={handleDownload} disabled={isDownloading}>
                {isDownloading ? <Spinner className="h-4 w-4 mr-2" /> : null}
                Download PDF
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  // Show loading state when loading existing draft
  if (isLoadingDraft) {
    return (
      <div className="p-6">
        <Card>
          <CardBody className="flex flex-col items-center gap-4 py-12">
            <Spinner className="h-8 w-8" />
            <Typography color="gray">Loading draft...</Typography>
          </CardBody>
        </Card>
      </div>
    );
  }

  // Show report editor if draft exists
  if (draft) {
    return (
      <div className="p-6 h-[calc(100vh-100px)]">
        <ReportEditor draft={draft} onDraftChange={setDraft} onFinalized={handleFinalized} />
      </div>
    );
  }

  // Show draft generation form
  return (
    <div className="p-6">
      <Card className="max-w-2xl mx-auto">
        <CardBody className="space-y-6">
          <Typography variant="h5" color="blue-gray">
            Generate New Report
          </Typography>

          {error && (
            <Alert color="red" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Customer */}
          <Input
            label="Customer Name *"
            value={reportParams.customerName}
            onChange={(e) =>
              setReportParams((prev) => ({
                ...prev,
                customerName: e.target.value,
              }))
            }
            placeholder="e.g., John Smith"
          />

          {/* Subject Aircraft */}
          <div className="space-y-4">
            <div>
              <Typography variant="small" className="font-semibold text-blue-gray-700">
                Subject Aircraft
              </Typography>
              <Typography variant="small" color="gray">
                Enter details for market analysis
              </Typography>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Registration *"
                value={reportParams.subjectAircraft.registration}
                onChange={(e) =>
                  setReportParams((prev) => ({
                    ...prev,
                    subjectAircraft: {
                      ...prev.subjectAircraft,
                      registration: e.target.value.toUpperCase(),
                    },
                  }))
                }
                placeholder="N123AB"
              />
              <Input
                type="number"
                label="Year *"
                value={reportParams.subjectAircraft.year}
                onChange={(e) =>
                  setReportParams((prev) => ({
                    ...prev,
                    subjectAircraft: {
                      ...prev.subjectAircraft,
                      year: e.target.value,
                    },
                  }))
                }
                placeholder="2019"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Typography variant="small" color="blue-gray" className="mb-1 font-medium">
                  Manufacturer *
                </Typography>
                <Select
                  options={manufacturerOptions}
                  value={selectedManufacturer}
                  onChange={handleManufacturerChange}
                  placeholder="Select..."
                  isClearable
                  styles={{
                    control: (base) => ({ ...base, minHeight: '40px' }),
                  }}
                />
              </div>
              <div>
                <Typography variant="small" color="blue-gray" className="mb-1 font-medium">
                  Model *
                </Typography>
                <Select
                  options={modelOptions}
                  value={selectedModel}
                  onChange={setSelectedModel}
                  placeholder={selectedManufacturer ? "Select..." : "Select manufacturer first"}
                  isDisabled={!selectedManufacturer}
                  isClearable
                  styles={{
                    control: (base) => ({ ...base, minHeight: '40px' }),
                  }}
                />
              </div>
            </div>

            <Input
              type="number"
              label="Total Time (hours) *"
              value={reportParams.subjectAircraft.totalTime}
              onChange={(e) =>
                setReportParams((prev) => ({
                  ...prev,
                  subjectAircraft: {
                    ...prev.subjectAircraft,
                    totalTime: e.target.value,
                  },
                }))
              }
              placeholder="1200"
            />

            <Textarea
              label="Additional Details (equipment, upgrades, condition...)"
              value={reportParams.subjectAircraftDetails}
              onChange={(e) =>
                setReportParams((prev) => ({
                  ...prev,
                  subjectAircraftDetails: e.target.value,
                }))
              }
              rows={2}
            />
          </div>

          {/* Analysis Settings */}
          <div className="grid grid-cols-2 gap-4">
            <MTSelect
              label="Year Range"
              value={String(reportParams.yearRange)}
              onChange={(val) =>
                setReportParams((prev) => ({
                  ...prev,
                  yearRange: parseInt(val, 10),
                }))
              }
            >
              {YEAR_RANGES.map((r) => (
                <Option key={String(r.value)} value={String(r.value)}>
                  {r.label}
                </Option>
              ))}
            </MTSelect>
            <MTSelect
              label="Analysis Period"
              value={String(reportParams.analysisConfig.periodDays)}
              onChange={(val) =>
                setReportParams((prev) => ({
                  ...prev,
                  analysisConfig: {
                    ...prev.analysisConfig,
                    periodDays: parseInt(val, 10),
                  },
                }))
              }
            >
              {PERIOD_OPTIONS.map((p) => (
                <Option key={p.value} value={String(p.value)}>
                  {p.label}
                </Option>
              ))}
            </MTSelect>
          </div>

          {/* Sections */}
          <div className="border rounded-lg">
            <div
              className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
              onClick={() => setShowSections(!showSections)}
            >
              <div className="flex items-center gap-2">
                <Typography variant="small" className="font-medium">
                  Report Sections
                </Typography>
                <Typography variant="small" color="gray">
                  ({reportParams.includedSections.length}/{AVAILABLE_SECTIONS.length})
                </Typography>
              </div>
              {showSections ? (
                <ChevronUpIcon className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronDownIcon className="h-4 w-4 text-gray-500" />
              )}
            </div>
            <Collapse open={showSections}>
              <div className="grid grid-cols-2 gap-2 p-3 pt-0 border-t">
                {AVAILABLE_SECTIONS.map((section) => (
                  <Checkbox
                    key={section.id}
                    label={section.label}
                    checked={reportParams.includedSections.includes(section.id)}
                    onChange={() => handleSectionToggle(section.id)}
                  />
                ))}
              </div>
            </Collapse>
          </div>

          <Button
            className="flex items-center justify-center gap-2"
            onClick={handleGenerateDraft}
            disabled={isGenerating || !isFormValid}
            fullWidth
          >
            {isGenerating ? (
              <Spinner className="h-4 w-4" />
            ) : (
              <DocumentPlusIcon className="h-5 w-5" />
            )}
            {isGenerating ? 'Generating...' : 'Generate Report'}
          </Button>
        </CardBody>
      </Card>
    </div>
  );
}

export default ReportEditorPage;
